import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  Table,
  Input,
  message,
  Popconfirm,
  Space,
  Typography,
  Tag,
  Pagination,
  Tooltip,
  Dropdown,
  Checkbox
} from "antd";
import { useSelector, useDispatch } from "react-redux";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  ReloadOutlined,
  FileImageOutlined,
  DownOutlined
} from "@ant-design/icons";
import { selectRole } from "modules/auth/store/authSlice";
import { UserRole } from "configs/constants";
import { selectAllPackageTypes } from "modules/client/package-types/store/slice";
import { getPackageTypeList } from "modules/client/package-types/store/thunks";
import warehouseApi from "../api/warehouseApi";
import "./WarehouseInventoryManager.scss";

const { Title, Text } = Typography;

const WarehouseInventoryManager = ({ warehouseId, warehouse }) => {
  const [inventoryItems, setInventoryItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deleteTooltipVisible, setDeleteTooltipVisible] = useState({});
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [measurementUnit, setMeasurementUnit] = useState("in");
  const role = useSelector(selectRole);
  const dispatch = useDispatch();
  const packageTypes = useSelector(selectAllPackageTypes);

  const getCategoryColor = (category) => {
    switch (category) {
      case "Keyboards":
        return "blue";
      case "Mouse":
        return "green";
      case "Electronics":
        return "purple";
      case "Office Supplies":
        return "orange";
      case "Tools":
        return "cyan";
      case "Other":
        return "default";
      default:
        return "geekblue";
    }
  };

  // Sample locations - in real app this would come from API
  const locations = [
    { id: "loc-1", name: "Main Warehouse" },
    { id: "loc-2", name: "Storage Room A" },
    { id: "loc-3", name: "Cold Storage" },
    { id: "loc-4", name: "Shipping Dock" }
  ];

  useEffect(() => {
    if (warehouseId) {
      loadInventoryItems();
      loadSettings();
      dispatch(getPackageTypeList());
    }
  }, [warehouseId]);

  const loadInventoryItems = async () => {
    setLoading(true);
    try {
      const items = await warehouseApi.getWarehouseInventoryItems(warehouseId);
      setInventoryItems(items);
    } catch (error) {
      console.error("Error loading inventory items:", error);
      message.error("Failed to load inventory items");
      setInventoryItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const settings = await warehouseApi.getSettings();
      setMeasurementUnit(settings.measurementUnit || "in");
    } catch (error) {
      console.error("Error loading settings:", error);
    }
  };

  // Filter items based on search
  useEffect(() => {
    if (!searchText.trim()) {
      setFilteredItems(inventoryItems);
    } else {
      const filtered = inventoryItems.filter(
        (item) =>
          item.name.toLowerCase().includes(searchText.toLowerCase()) ||
          item.itemId.toLowerCase().includes(searchText.toLowerCase()) ||
          item.description.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredItems(filtered);
    }
    setCCurrentPage(1); // Reset to first page when searching
  }, [inventoryItems, searchText]);

  const handleEdit = (item) => {
    // Always navigate to dedicated edit page
    window.location.href = `/warehouses/${warehouseId}/edit/${item.id}`;
  };

  const handleDelete = async (itemId) => {
    try {
      await warehouseApi.deleteInventoryItem(itemId);
      message.success("Item deleted successfully");
      loadInventoryItems();
    } catch (error) {
      console.error("Error deleting item:", error);
      message.error("Failed to delete item");
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(
        selectedRowKeys.map((id) => warehouseApi.deleteInventoryItem(id))
      );
      message.success(`${selectedRowKeys.length} items deleted successfully`);
      setSelectedRowKeys([]);
      loadInventoryItems();
    } catch (error) {
      console.error("Error deleting items:", error);
      message.error("Failed to delete items");
    }
  };

  const handleCreateRoute = () => {
    message.info(`Creating route with ${selectedRowKeys.length} selected items`);
  };

  // Handle view details - navigate to dedicated page
  const handleViewDetails = (item) => {
    window.location.href = `/warehouses/${warehouseId}/item-details/${item.id}`;
  };

  // Get location name
  const getLocationName = (locationId) => {
    const location = locations.find((loc) => loc.id === locationId);
    return location ? location.name : "Unknown Location";
  };

  const columns = [
    {
      title: "Item ID",
      dataIndex: "itemId",
      key: "itemId",
      width: 120,
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: "Image",
      key: "image",
      width: 80,
      align: "center",
      render: (_, record) => (
        <div>
          {record.image ? (
            <img
              src={record.image}
              alt={record.name}
              style={{
                width: "50px",
                height: "50px",
                objectFit: "cover",
                borderRadius: "6px",
                border: "1px solid #d9d9d9"
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <div
              style={{
                width: "50px",
                height: "50px",
                backgroundColor: "#fafafa",
                borderRadius: "6px",
                border: "2px dashed #d9d9d9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <FileImageOutlined style={{ fontSize: "20px", color: "#bfbfbf" }} />
            </div>
          )}
        </div>
      )
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      ellipsis: true
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      width: 200
    },
    {
      title: "Package Type",
      dataIndex: "packageTypeId",
      key: "packageTypeId",
      width: 120,
      render: (packageTypeId) => {
        if (!packageTypeId) return <Tag color="blue">None</Tag>;
        const pkgType = packageTypes.find((t) => t.id === packageTypeId);
        if (!pkgType) return <Tag color="blue">None</Tag>;
        const tooltipContent =
          pkgType && (pkgType.maxWeight || pkgType.maxLength) ? (
            <span>
              {pkgType.maxWeight && `${pkgType.maxWeight}kg`}
              {pkgType.maxWeight && pkgType.maxLength && " · "}
              {pkgType.maxLength &&
                (() => {
                  const displayLength =
                    measurementUnit === "in"
                      ? (pkgType.maxLength / 2.54).toFixed(1)
                      : pkgType.maxLength;
                  return `${displayLength}${measurementUnit}`;
                })()}
            </span>
          ) : null;

        return tooltipContent ? (
          <Tooltip title={tooltipContent}>
            <Tag color="blue">{pkgType.name}</Tag>
          </Tooltip>
        ) : (
          <Tag color="blue">{pkgType.name}</Tag>
        );
      }
    },
    {
      title: "Type",
      dataIndex: "isFungible",
      key: "isFungible",
      width: 100,
      render: (isFungible) => (
        <Tag color={!isFungible ? "blue" : "default"}>
          {!isFungible ? "Unique" : "Non-Unique"}
        </Tag>
      )
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: 150,
      render: (locationId) => getLocationName(locationId)
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 80,
      align: "center",
      render: (quantity) => <Text strong>{quantity}</Text>
    },
    {
      title: "Unit Price",
      dataIndex: "unitPrice",
      key: "unitPrice",
      width: 100,
      align: "right",
      render: (price) => `$${parseFloat(price || 0).toFixed(2)}`
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          {role !== UserRole.VIEWER && (
            <Tooltip title="Edit" mouseLeaveDelay={0}>
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(record);
                }}
                size="small"
                className="action-btn edit-btn"
              />
            </Tooltip>
          )}
          {role !== UserRole.VIEWER && (
            <Popconfirm
              title="Are you sure you want to delete this item?"
              description="This action cannot be undone."
              onConfirm={(e) => {
                e?.stopPropagation();
                handleDelete(record.id);
              }}
              onCancel={(e) => {
                e?.stopPropagation();
              }}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip
                title="Delete"
                open={deleteTooltipVisible[record.id]}
                onOpenChange={(open) => {
                  setDeleteTooltipVisible((prev) => ({ ...prev, [record.id]: open }));
                }}
              >
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteTooltipVisible((prev) => ({ ...prev, [record.id]: false }));
                  }}
                  onMouseEnter={() =>
                    setDeleteTooltipVisible((prev) => ({ ...prev, [record.id]: true }))
                  }
                  onMouseLeave={() =>
                    setDeleteTooltipVisible((prev) => ({ ...prev, [record.id]: false }))
                  }
                  size="small"
                  className="action-btn delete-btn"
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  // Pagination config
  const paginationConfig = {
    current: currentPage,
    pageSize: pageSize,
    total: filteredItems.length,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
    onChange: (page, size) => {
      setCCurrentPage(page);
      setPageSize(size);
    }
  };

  // Get current page data
  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredItems.slice(startIndex, endIndex);
  };

  return (
    <div className="client-warehouses-list">
      {/* Page Header - consistent with other tabs */}
      <div className="page-header">
        <div className="page-title">
          <h2 className="title">Inventory Items</h2>
        </div>
        <div className="page-actions">
          {role !== UserRole.VIEWER && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                // Always navigate to dedicated creation page
                if (warehouseId) {
                  window.location.href = `/warehouses/inventory/creation/${warehouseId}`;
                } else {
                  message.error("Warehouse context is missing. Please refresh the page.");
                }
              }}
              className="add-warehouse-btn"
            >
              Add Item
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="table-wrapper">
        <div className="table-header">
          <div className="table-controls-left">
            <Space>
              <Input.Search
                placeholder="Search items..."
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="search-input"
                style={{ width: 250 }}
              />
              <Button
                type="text"
                icon={<ReloadOutlined />}
                className="control-btn"
                title="Refresh"
                onClick={() => window.location.reload()}
              />
            </Space>
          </div>

          <div className="table-controls-right">
            <Button
              type="text"
              icon={<SettingOutlined />}
              className="control-btn"
              title="Settings"
            />
          </div>
        </div>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={filteredItems}
          rowKey="id"
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
            columnTitle: (
              <Space size="small">
                <Tooltip title="Select All">
                  <Checkbox
                    checked={
                      selectedRowKeys.length > 0 &&
                      selectedRowKeys.length === filteredItems.length
                    }
                    indeterminate={
                      selectedRowKeys.length > 0 &&
                      selectedRowKeys.length < filteredItems.length
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRowKeys(filteredItems.map((item) => item.id));
                      } else {
                        setSelectedRowKeys([]);
                      }
                    }}
                  />
                </Tooltip>
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: "header",
                        label: <Text strong>{selectedRowKeys.length} selected</Text>,
                        disabled: true,
                        style: { cursor: "default" }
                      },
                      { type: "divider" },
                      {
                        key: "clear",
                        label: (
                          <Tooltip title="Deselect the items" placement="right">
                            <span>Clear selection</span>
                          </Tooltip>
                        ),
                        onClick: () => setSelectedRowKeys([]),
                        disabled: selectedRowKeys.length === 0
                      },
                      {
                        key: "create-route",
                        label: (
                          <Tooltip
                            title="Create route with selected items"
                            placement="right"
                          >
                            <span>Create Route</span>
                          </Tooltip>
                        ),
                        onClick: handleCreateRoute,
                        disabled: selectedRowKeys.length === 0
                      },
                      {
                        key: "delete",
                        label: (
                          <Tooltip title="Deletes selected items" placement="right">
                            <span>Delete</span>
                          </Tooltip>
                        ),
                        onClick: handleBulkDelete,
                        disabled: selectedRowKeys.length === 0
                      }
                    ]
                  }}
                  trigger={["click"]}
                >
                  <Button size="small" type="text">
                    Bulk Actions
                    <DownOutlined />
                  </Button>
                </Dropdown>
              </Space>
            )
          }}
          onRow={(record) => ({
            onClick: (e) => {
              if (!e.target.closest(".ant-table-cell-fix-right")) {
                console.log("📎 Inventory row clicked:", record.name);
                handleViewDetails(record);
              }
            }
          })}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `Showing ${range[0]} to ${range[1]} of ${total} entries`,
            defaultPageSize: 10,
            pageSizeOptions: ["10", "25", "50", "100"]
          }}
          scroll={{ x: "max-content" }}
          size="small"
          className="warehouses-table"
        />
      </div>
    </div>
  );
};

export default WarehouseInventoryManager;
